import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Notification from 'components/UserNotifications/Notification';
import { cancelAllNotificationsRequest, getUserNotifications } from 'domains/user';
import { CenterFlex, Flex, InfiniteScroll, Pipeline, Text, useInfiniteScroll } from 'UI';
import { getSearchData } from 'utils';

const NOTIFICATIONS_CHUNK_SIZE = 50;

const UserNotifications = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userNotifications = useSelector(getUserNotifications);
  const notificationsData = getSearchData(userNotifications)?.notifications ?? [];
  const scrollParentRef = useRef<HTMLDivElement>(null);
  const { hasMore, loadMore, currentData } = useInfiniteScroll({
    data: notificationsData,
    chunkSize: NOTIFICATIONS_CHUNK_SIZE,
  });
  const getScrollParent = useCallback(() => scrollParentRef.current, []);

  const handleCancelAll = () => {
    dispatch(cancelAllNotificationsRequest());
  };

  return (
    <Flex ref={scrollParentRef} direction={'column'} minWidth={500} maxWidth={500} maxHeight={480} overflowY={'scroll'}>
      <Flex justify={'space-between'} padding={'10px 20px 10px 20px'}>
        <Text type="lg" bold disableGutter>
          {t('notifications.title', 'Notifications')}
        </Text>
        <Text
          type="base"
          color="red"
          disableGutter
          cursor={'pointer'}
          hoverUnderLine
          onClick={handleCancelAll}
          data-cy={'clear-all-button'}
        >
          {t('notifications.action.clear_all', 'Clear all')}
        </Text>
      </Flex>
      <Pipeline size={'100%'} horizontal />
      <Flex direction={'column'} padding={'10px 20px 10px 20px'}>
        {notificationsData.length > 0 ? (
          <InfiniteScroll hasMore={hasMore} loadMore={loadMore} getScrollParent={getScrollParent} useWindow={false}>
            <Flex direction={'column'} gap={20}>
              {currentData.map((notification) => (
                <Notification key={notification.notificationId} notification={notification} />
              ))}
            </Flex>
          </InfiniteScroll>
        ) : (
          <CenterFlex>
            <Text type="base">{t('notifications.no_unread_notifications', 'No unread notifications')}</Text>
          </CenterFlex>
        )}
      </Flex>
    </Flex>
  );
};

export default UserNotifications;
